import { DistributedLoad, LoadType } from 'types/domainTypes';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { EditDialog } from 'components/EditDialog/EditDialog';
import { Alert } from '@mui/material';
import { NumericTextField } from 'components/FormFields/NumericTextField';
import { ReactElement } from 'react';

export type DistributedLoadFormData = {
  startLoad: number | '';
  endLoad: number | '';
  startLocation: number | '';
  endLocation: number | '';
  id?: string;
};

interface DistributedLoadDialogProps {
  formData?: DistributedLoad;
  title: string;
  loadType: LoadType;
  onClose: () => void;
  onSave: (formData: DistributedLoadFormData) => void;
  maxLocation: number;
}

export function DistributedLoadDialog({
  formData,
  title,
  loadType,
  onClose,
  onSave,
  maxLocation,
}: DistributedLoadDialogProps): ReactElement {
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { isDirty },
    watch,
  } = useForm<DistributedLoadFormData>({
    reValidateMode: 'onChange',
    defaultValues: {
      startLoad: formData?.loadStart !== undefined ? formData?.loadStart : '',
      endLoad: formData?.loadEnd !== undefined ? formData?.loadEnd : '',
      startLocation: formData?.positionStart !== undefined ? formData?.positionStart : '',
      endLocation: formData?.positionEnd !== undefined ? formData?.positionEnd : '',
      id: formData?.id,
    },
  });

  const startLocation = watch('startLocation');
  const submitHandler = (submitData: DistributedLoadFormData) => {
    if (loadType === LoadType.SuctionLoad) {
      submitData.startLoad = -Math.abs(submitData.startLoad as number);
      submitData.endLoad = -Math.abs(submitData.endLoad as number);
    }

    onSave(submitData);
  };

  return (
    <EditDialog
      title={title}
      onClose={onClose}
      handleFormSubmit={handleSubmit(submitHandler)}
      isDirty={isDirty}
      saveActionText={t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.save-action-text')}
    >
      <Alert severity="info">{t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.alert')}</Alert>
      {loadType === LoadType.PermanentLoad && (
        <Alert severity="info">{t('calculation-page.spans-and-loads-dialog.removable-load-alert')}</Alert>
      )}
      <NumericTextField
        data-testid={'distributed-load-location-start-test-id'}
        name="startLocation"
        required
        control={control}
        label={t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.label-location-start')}
        fullWidth
        decimalPlaces={3}
        adornmentProps={{ endAdornment: 'm' }}
        allowZero
        maxValue={{
          value: maxLocation,
          errorText: t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.max-value-error', { maxLocation: maxLocation }),
        }}
      />
      <NumericTextField
        data-testid={'distributed-load-location-end-test-id'}
        name="endLocation"
        required
        control={control}
        label={t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.label-location-end')}
        fullWidth
        adornmentProps={{ endAdornment: 'm' }}
        decimalPlaces={3}
        maxValue={{
          value: maxLocation,
          errorText: t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.max-value-error', { maxLocation: maxLocation }),
        }}
        minValue={
          startLocation !== ''
            ? {
                value: startLocation + 0.001,
                errorText: t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.min-value-error'),
              }
            : undefined
        }
      />
      <NumericTextField
        data-testid={'distributed-load-start-test-id'}
        name="startLoad"
        required
        control={control}
        label={t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.label-load-start')}
        fullWidth
        adornmentProps={{ endAdornment: 'kN/m²' }}
        decimalPlaces={3}
        requiredTextAdornment={
          loadType === LoadType.SuctionLoad
            ? t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.required-text-adornment')
            : undefined
        }
        allowZero
        allowNegative={loadType === LoadType.SuctionLoad}
      />
      <NumericTextField
        data-testid={'distributed-load-end-test-id'}
        name="endLoad"
        required
        control={control}
        label={t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.label-load-end')}
        fullWidth
        adornmentProps={{ endAdornment: 'kN/m²' }}
        decimalPlaces={3}
        requiredTextAdornment={
          loadType === LoadType.SuctionLoad
            ? t('calculation-page.spans-and-loads-dialog.distributed-load-dialog.required-text-adornment')
            : undefined
        }
        allowZero
        allowNegative={loadType === LoadType.SuctionLoad}
      />
    </EditDialog>
  );
}
